import { useState } from "react";
import { api } from "../lib/api";
import {
  format, startOfMonth, startOfWeek, addDays, addMonths, addWeeks,
  isSameDay, isSameMonth,
} from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, Save, Clock, Truck } from "lucide-react";

const STATUSES = [
  { v: "in_afwachting", l: "Aangevraagd", c: "bg-yellow-600/20 text-yellow-400 border-yellow-700" },
  { v: "bevestigd", l: "Bevestigd", c: "bg-green-600/20 text-green-400 border-green-700" },
  { v: "in_uitvoering", l: "In uitvoering", c: "bg-blue-600/20 text-blue-400 border-blue-700" },
  { v: "voltooid", l: "Voltooid", c: "bg-[#2A2B31] text-[#B8B9BE] border-[#3A3B41]" },
  { v: "geannuleerd", l: "Geannuleerd", c: "bg-red-600/15 text-red-400 border-red-800" },
];
const statusInfo = (v) => STATUSES.find((s) => s.v === v) || STATUSES[0];
const SERVICES = ["Onderhoud", "Herstelling", "Diagnose", "Banden vervangen", "Aankoopcontrole", "Andere"];
const fld = "w-full bg-[#0A0A0B] border border-[#2A2B31] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#E10600]";
const lbl = "font-mono-tt text-[11px] uppercase tracking-wider text-[#9A9CA3] block mb-1";

function AptChip({ a, onClick }) {
  const si = statusInfo(a.status);
  return (
    <button onClick={onClick} data-testid={`agenda-apt-${a.id}`}
      className={`w-full text-left text-[11px] leading-tight px-2 py-1 rounded border ${si.c} truncate hover:opacity-80`}>
      <span className="font-mono-tt">{a.tijdstip || "—"}</span> {a.naam}
    </button>
  );
}

function EditModal({ apt, onClose, onSaved }) {
  const isNew = !apt.id;
  const [f, setF] = useState({
    naam: "", email: "", telefoon: "", service: "Onderhoud", datum: format(new Date(), "yyyy-MM-dd"),
    tijdstip: "09:00", duur: 60, wacht: false, ophalen: false, status: "bevestigd", opmerkingen: "", ...apt,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/appointments/admin", { ...f, duur: parseInt(f.duur) || 60 });
      else await api.put(`/appointments/${apt.id}`, { ...f, duur: parseInt(f.duur) || 60 });
      onSaved();
    } finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm("Deze afspraak verwijderen?")) return;
    await api.delete(`/appointments/${apt.id}`); onSaved();
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141518] border border-[#2A2B31] w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="agenda-edit-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2B31]">
          <h3 className="font-display uppercase font-700 text-white">{isNew ? "Nieuwe afspraak" : "Afspraak bewerken"}</h3>
          <button onClick={onClose} className="text-[#9A9CA3] hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Naam</label><input data-testid="edit-naam" value={f.naam} onChange={(e) => set("naam", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Telefoon</label><input value={f.telefoon} onChange={(e) => set("telefoon", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>E-mail</label><input value={f.email} onChange={(e) => set("email", e.target.value)} className={fld} /></div>
            <div>
              <label className={lbl}>Dienst</label>
              <select value={f.service} onChange={(e) => set("service", e.target.value)} className={fld}>
                {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label className={lbl}>Datum</label><input data-testid="edit-datum" type="date" value={f.datum} onChange={(e) => set("datum", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Tijdstip</label><input data-testid="edit-tijdstip" type="time" value={f.tijdstip} onChange={(e) => set("tijdstip", e.target.value)} className={fld} /></div>
            <div><label className={lbl}>Duur (min)</label><input type="number" step="15" value={f.duur} onChange={(e) => set("duur", e.target.value)} className={fld} /></div>
            <div>
              <label className={lbl}>Status</label>
              <select data-testid="edit-status" value={f.status} onChange={(e) => set("status", e.target.value)} className={fld}>
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-[#B8B9BE]"><input type="checkbox" checked={!!f.wacht} onChange={(e) => set("wacht", e.target.checked)} /> Klant wacht erop</label>
            <label className="flex items-center gap-2 text-sm text-[#B8B9BE]"><input type="checkbox" checked={!!f.ophalen} onChange={(e) => set("ophalen", e.target.checked)} /> Ophalen (transport)</label>
          </div>
          <div><label className={lbl}>Opmerkingen</label><textarea rows={3} value={f.opmerkingen} onChange={(e) => set("opmerkingen", e.target.value)} className={fld} /></div>
        </div>
        <div className="flex items-center gap-3 px-5 py-4 border-t border-[#2A2B31]">
          {!isNew && <button onClick={del} data-testid="edit-delete" className="text-[#E10600] hover:text-red-400 flex items-center gap-1 text-sm mr-auto"><Trash2 size={15} /> Verwijderen</button>}
          <button onClick={onClose} className="border border-[#2A2B31] text-white px-4 py-2 text-sm font-display uppercase ml-auto">Annuleer</button>
          <button onClick={save} disabled={saving || !f.naam} data-testid="edit-save" className="bg-[#E10600] hover:bg-[#B80500] disabled:opacity-50 text-white px-4 py-2 text-sm font-display uppercase font-700 flex items-center gap-1"><Save size={15} /> Opslaan</button>
        </div>
      </div>
    </div>
  );
}

export default function AgendaBeheer({ appointments, reload }) {
  const [view, setView] = useState("maand");
  const [cursor, setCursor] = useState(new Date());
  const [edit, setEdit] = useState(null);

  const dayApts = (date) => appointments
    .filter((a) => a.datum === format(date, "yyyy-MM-dd"))
    .sort((a, b) => (a.tijdstip || "").localeCompare(b.tijdstip || ""));

  const shift = (dir) => {
    if (view === "maand") setCursor(addMonths(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addDays(cursor, dir));
  };

  const title = view === "maand"
    ? format(cursor, "MMMM yyyy", { locale: nl })
    : view === "week"
      ? `Week van ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM", { locale: nl })}`
      : format(cursor, "EEEE d MMMM yyyy", { locale: nl });

  return (
    <div data-testid="agenda-beheer">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="heading-xl text-2xl text-white">Agenda</h2>
        <div className="flex border border-[#2A2B31] rounded overflow-hidden ml-auto">
          {["dag", "week", "maand"].map((v) => (
            <button key={v} data-testid={`agenda-view-${v}`} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-xs font-display uppercase ${view === v ? "bg-[#E10600] text-white" : "text-[#9A9CA3] hover:text-white"}`}>{v}</button>
          ))}
        </div>
        <button data-testid="agenda-new" onClick={() => setEdit({ datum: format(cursor, "yyyy-MM-dd") })}
          className="bg-[#E10600] hover:bg-[#B80500] text-white px-4 py-1.5 text-xs font-display uppercase font-700 flex items-center gap-1"><Plus size={14} /> Nieuwe afspraak</button>
      </div>

      <div className="flex items-center justify-between mb-4">
        <button onClick={() => shift(-1)} className="border border-[#2A2B31] hover:border-white text-white p-2 rounded"><ChevronLeft size={18} /></button>
        <p className="font-display uppercase text-white text-lg capitalize" data-testid="agenda-title">{title}</p>
        <button onClick={() => shift(1)} className="border border-[#2A2B31] hover:border-white text-white p-2 rounded"><ChevronRight size={18} /></button>
      </div>

      {/* MAAND */}
      {view === "maand" && (
        <div className="grid grid-cols-7 gap-px bg-[#2A2B31] border border-[#2A2B31]">
          {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((d) => (
            <div key={d} className="bg-[#141518] text-center py-2 text-[11px] font-mono-tt uppercase text-[#9A9CA3]">{d}</div>
          ))}
          {Array.from({ length: 42 }).map((_, i) => {
            const day = addDays(startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), i);
            const apts = dayApts(day);
            const dim = !isSameMonth(day, cursor);
            const todayFlag = isSameDay(day, new Date());
            return (
              <div key={i} onClick={() => { setCursor(day); setView("dag"); }}
                className={`bg-[#0A0A0B] min-h-[92px] p-1.5 cursor-pointer hover:bg-[#141518] ${dim ? "opacity-40" : ""}`}>
                <div className={`text-xs mb-1 ${todayFlag ? "text-[#E10600] font-700" : "text-[#B8B9BE]"}`}>{format(day, "d")}</div>
                <div className="space-y-1">
                  {apts.slice(0, 3).map((a) => <AptChip key={a.id} a={a} onClick={(e) => { e.stopPropagation(); setEdit(a); }} />)}
                  {apts.length > 3 && <div className="text-[10px] text-[#9A9CA3]">+{apts.length - 3} meer</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* WEEK */}
      {view === "week" && (
        <div className="grid grid-cols-7 gap-px bg-[#2A2B31] border border-[#2A2B31]">
          {Array.from({ length: 7 }).map((_, i) => {
            const day = addDays(startOfWeek(cursor, { weekStartsOn: 1 }), i);
            const apts = dayApts(day);
            const todayFlag = isSameDay(day, new Date());
            return (
              <div key={i} className="bg-[#0A0A0B] min-h-[220px] p-2">
                <div className={`text-xs mb-2 capitalize ${todayFlag ? "text-[#E10600] font-700" : "text-[#B8B9BE]"}`}>{format(day, "EEE d", { locale: nl })}</div>
                <div className="space-y-1.5">
                  {apts.map((a) => <AptChip key={a.id} a={a} onClick={() => setEdit(a)} />)}
                  {apts.length === 0 && <div className="text-[10px] text-[#5A5C63]">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DAG */}
      {view === "dag" && (
        <div className="space-y-2">
          {dayApts(cursor).map((a) => {
            const si = statusInfo(a.status);
            return (
              <div key={a.id} onClick={() => setEdit(a)} data-testid={`agenda-apt-${a.id}`}
                className="bg-[#141518] border border-[#2A2B31] p-4 flex items-center gap-4 cursor-pointer hover:border-[#E10600]/50">
                <div className="text-center shrink-0 w-16">
                  <div className="text-white font-display text-lg">{a.tijdstip || "—"}</div>
                  <div className="text-[10px] text-[#5A5C63] font-mono-tt flex items-center gap-1 justify-center"><Clock size={10} /> {a.duur || 60}m</div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-600 truncate">{a.naam} <span className="text-[#9A9CA3] font-normal">· {a.service === "Andere" ? a.service_andere : a.service}</span></p>
                  <p className="text-xs text-[#9A9CA3] truncate">{a.telefoon} {a.merk && `· ${a.merk} ${a.model || ""}`} {a.wacht ? "· wacht erop" : "· afgeven"} {a.ophalen && <span className="text-[#E10600] inline-flex items-center gap-0.5">· <Truck size={11} /> ophalen</span>}</p>
                </div>
                <span className={`text-[10px] font-mono-tt uppercase px-2 py-0.5 border shrink-0 ${si.c}`}>{si.l}</span>
              </div>
            );
          })}
          {dayApts(cursor).length === 0 && <p className="text-[#9A9CA3] py-8 text-center">Geen afspraken op deze dag.</p>}
        </div>
      )}

      {edit && <EditModal apt={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
